import React, { useState } from 'react';
import { exportScopeReport } from './ScopeReport';
import { calcProductionSummary } from './calcUtils';
import { ACCESS_CATEGORIES } from './AccessTags';
import { showToast } from './Toast';
import Icon from './Icons';

// Default context sentences — editable by user before export
const DEFAULT_ACCESS_CONTEXT = {
  'Internal Access': 'The following internal access requirements have been identified for this area:',
  'Internal Conditions': 'The following internal conditions apply to this area:',
  'External Access & Height': 'The following access and working-at-height requirements have been identified for this area:',
  'Site Hazards': 'The following site hazards have been identified and must be managed:',
  'Building & Condition': 'The following building condition issues have been noted:',
  'Site Conditions': 'The following site conditions apply to this area:',
};


// ─── ScopePDFPreview ────────────────────────────────────────────────────────
// Full-screen editor that lets users refine project data before PDF generation.
// Works on a local copy — does not save back to Firestore.

function generateCoverNote(project, areas, hoursSource) {
  const allAreas = areas || project.areas || [];
  const internal = allAreas.filter(a => a.type === 'internal');
  const external = allAreas.filter(a => a.type === 'external');
  const hasInt = internal.length > 0;
  const hasExt = external.length > 0;
  const src = hoursSource || 'measured';

  const ps = calcProductionSummary(allAreas);
  const washRate = project.washRate || 75;
  const washHrs = ps.external.m2 > 0 ? ps.external.m2 / washRate : 0;

  // Calculate total hours based on selected source
  let totalHrs = 0;
  if (src === 'approved') {
    totalHrs = allAreas.reduce((s, a) => s + (parseFloat(a.approvedHours) || 0), 0);
    if (totalHrs === 0) totalHrs = ps.grandTotalHours; // fallback
  } else if (src === 'assessed') {
    totalHrs = allAreas.reduce((s, a) => {
      const ah = a.assessBySubstrate ? Object.values(a.assessBySubstrate).reduce((ss, v) => ss + (parseFloat(v.hours) || 0), 0) : 0;
      return s + (ah > 0 ? ah : 0);
    }, 0);
    if (totalHrs === 0) totalHrs = ps.grandTotalHours; // fallback
  } else {
    totalHrs = ps.grandTotalHours + washHrs;
  }

  // ── Paragraph 1: Scope overview ──
  let scopeType = '';
  if (hasInt && hasExt) scopeType = 'internal and external painting works';
  else if (hasInt) scopeType = 'internal painting works';
  else if (hasExt) scopeType = 'external painting works';
  else scopeType = 'painting works';

  const address = project.address ? ` at ${project.address}` : '';
  const estimator = project.estimatorName ? ` by ${project.estimatorName}` : '';
  const date = project.date ? project.date.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1') : new Date().toLocaleDateString('en-AU');

  const intNames = internal.map(a => a.name).filter(Boolean);
  const extNames = external.map(a => a.name).filter(Boolean);
  let areaList = '';
  if (intNames.length > 0 || extNames.length > 0) {
    const parts = [];
    if (intNames.length > 0) parts.push(`${intNames.length} internal area${intNames.length > 1 ? 's' : ''} (${intNames.join(', ')})`);
    if (extNames.length > 0) parts.push(`${extNames.length} external area${extNames.length > 1 ? 's' : ''} (${extNames.join(', ')})`);
    areaList = ` Scope includes ${parts.join(' and ')}.`;
  }

  let p1 = `Site assessment for ${scopeType}${address}, completed ${date}${estimator}.${areaList}`;

  // ── Paragraph 2: Production summary — uses selected hours ──
  let p2 = '';
  if (totalHrs > 0) {
    const srcLabel = src === 'approved' ? 'Estimated' : src === 'assessed' ? 'Assessed' : 'Estimated';
    p2 = `${srcLabel} ${totalHrs.toFixed(0)} hours total labour and ${ps.totalLitres.toFixed(0)} litres of paint.`;
    if (src === 'measured' && washHrs > 0) p2 += ` External wash estimated at ${washHrs.toFixed(0)} hours.`;
  }

  // ── Paragraph 3: Key scope notes (deduplicated) ──
  const allNotes = [...new Set(
    allAreas.map(a => a.scopeNotes?.trim()).filter(Boolean)
  )];
  let p3 = '';
  if (allNotes.length > 0) {
    if (allNotes.length === 1) {
      p3 = `Paint system: ${allNotes[0]}`;
    } else {
      p3 = `Paint systems:\n${allNotes.map(n => `• ${n}`).join('\n')}`;
    }
  }

  // ── Paragraph 4: Exclusions summary ──
  const exclAreas = allAreas.filter(a => a.scopeExclusions?.trim());
  let p4 = '';
  if (exclAreas.length > 0) {
    const exclNames = exclAreas.map(a => a.name || 'Unnamed').join(', ');
    p4 = `Exclusions apply to ${exclAreas.length} area${exclAreas.length > 1 ? 's' : ''} (${exclNames}) — see area breakdown below.`;
  }

  // ── Defects flag ──
  const defectAreas = allAreas.filter(a => a.defectsNote?.trim());
  let p5 = '';
  if (defectAreas.length > 0) {
    const defectNames = defectAreas.map(a => a.name || 'Unnamed').join(', ');
    p5 = `Defects or remediation noted in ${defectAreas.length} area${defectAreas.length > 1 ? 's' : ''} (${defectNames}) — review before commencing.`;
  }

  // ── Closing ──
  const closing = `All measurements are approximate and subject to confirmation on commencement.`;

  return [p1, p2, p3, p4, p5, closing].filter(Boolean).join('\n\n');
}

function ScopePDFPreview({ project, onClose }) {
  const [coverNote, setCoverNote]   = useState(() => generateCoverNote(project));
  const [areas, setAreas]           = useState(() =>
    (project.areas || []).map(a => ({ ...a, _include: true, photos: [...(a.photos || [])] }))
  );
  const [exporting, setExporting]   = useState(false);
  const [statusMsg, setStatusMsg]   = useState('');
  const [expandedAreas, setExpandedAreas] = useState({});
  const [hoursSource, setHoursSource] = useState('measured');

  // ── Display option toggles ──────────────────────────────────────────────────
  const [showM2, setShowM2]                     = useState(true);
  const [showHours, setShowHours]               = useState(true);
  const [showLitres, setShowLitres]             = useState(true);
  const [showSurfaceDetail, setShowSurfaceDetail] = useState(true);

  // ── Duration state ──────────────────────────────────────────────────────────
  const [washRate, setWashRate]               = useState(75);
  const [accessContexts, setAccessContexts]   = useState(() => ({ ...DEFAULT_ACCESS_CONTEXT }));

  // Check if assessed/approved data exists
  const hasAssessed = areas.some(a => a.assessBySubstrate && Object.values(a.assessBySubstrate).some(v => parseFloat(v.hours) > 0));
  const hasApproved = areas.some(a => parseFloat(a.approvedHours) > 0);

  // ── Scope of Works items removed — doc is now ops/measure check only
  const moveArea = (idx, dir) => {
    const next = [...areas];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setAreas(next);
  };

  const toggleInclude = (idx) => {
    setAreas(prev => prev.map((a, i) => i === idx ? { ...a, _include: !a._include } : a));
  };

  const updateNotes = (idx, val) => {
    setAreas(prev => prev.map((a, i) => i === idx ? { ...a, scopeNotes: val } : a));
  };

  const removePhoto = (areaIdx, photoId) => {
    setAreas(prev => prev.map((a, i) =>
      i === areaIdx ? { ...a, photos: a.photos.filter(p => p.id !== photoId) } : a
    ));
  };

  // Mark a photo as the project cover hero — clears hero flag from ALL other photos across all areas
  const setHeroPhoto = (areaIdx, photoId) => {
    setAreas(prev => prev.map((a, ai) => ({
      ...a,
      photos: a.photos.map(p => ({
        ...p,
        isProjectHero: ai === areaIdx && p.id === photoId ? !p.isProjectHero : false,
      })),
    })));
  };


  const toggleExpand = (idx) => {
    setExpandedAreas(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // ── Generate PDF ───────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setExporting(true);
    setStatusMsg('Preparing...');
    try {
      const exportProject = {
        ...project,
        coverNote,
        washRate,
        hoursSource,
        accessContexts,
        showM2,
        showHours,
        showLitres,
        showSurfaceDetail,
        areas: areas.filter(a => a._include).map(({ _include, ...rest }) => rest),
      };
      await exportScopeReport(exportProject, msg => setStatusMsg(msg));
      setStatusMsg('PDF downloaded ✓');
      setTimeout(() => { setExporting(false); onClose(); }, 1500);
    } catch (err) {
      showToast('PDF export failed: ' + err.message, 'error');
      setExporting(false);
    }
  };

  const includedCount = areas.filter(a => a._include).length;

  return (
    <div className="sp-overlay">
      {/* ── Header ── */}
      <div className="sp-header">
        <button className="sp-back" onClick={onClose}>← Back</button>
        <div className="sp-header-title">
          <div className="sp-title">PDF Preview & Edit</div>
          <div className="sp-subtitle">{includedCount} of {areas.length} areas included</div>
        </div>
        <button
          className="sp-generate-btn"
          onClick={handleGenerate}
          disabled={exporting || includedCount === 0}
        >
          {exporting ? <><span className="spinner spinner-sm spinner-white"></span> {statusMsg}</> : <><Icon name="file" size={14} /> Generate PDF</>}
        </button>
      </div>

      <div className="sp-body">

        {/* ── Cover details ── */}
        <div className="sp-section">
          <div className="sp-section-title">📋 Cover Page</div>

          {/* Cover note */}
          <div className="sp-field">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <label>Cover Note <span className="sp-optional">editable — will appear on the PDF cover page</span></label>
              <button className="sp-regen-btn" onClick={() => setCoverNote(generateCoverNote(project, areas.filter(a => a._include), hoursSource))}>↻ Regenerate</button>
            </div>
            <textarea
              className="sp-textarea"
              rows={4}
              placeholder="e.g. This scope covers all internal and external repainting works at the above address..."
              value={coverNote}
              onChange={e => setCoverNote(e.target.value)}
            />
          </div>

          {/* Facts table */}
          {hasApproved && (
            <div className="sp-facts">
              <div className="sp-facts-title">✅ Estimated Hours Summary</div>
              <table className="sp-facts-table">
                <thead><tr><th>Area</th><th>Type</th><th>Estimated</th></tr></thead>
                <tbody>
                  {areas.filter(a => a._include && parseFloat(a.approvedHours) > 0).map(a => (
                    <tr key={a.firestoreId || a.id}>
                      <td>{a.name || 'Unnamed'}</td>
                      <td>{a.type === 'internal' ? '🏠' : '🏗️'}</td>
                      <td style={{fontWeight:700,color:'#059669'}}>{parseFloat(a.approvedHours).toFixed(1)}h</td>
                    </tr>
                  ))}
                  <tr style={{borderTop:'2px solid #e2e8f0',fontWeight:700}}>
                    <td>Total</td><td></td>
                    <td style={{color:'#059669'}}>{areas.filter(a => a._include).reduce((s,a) => s + (parseFloat(a.approvedHours)||0), 0).toFixed(1)}h</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Hours Source ── */}
        <div className="sp-section">
          <div className="sp-section-title">⏱ Hours to Display</div>
          <p className="sp-hint">Choose which hours appear in the PDF. Select "Scope Only" to produce a scope document without any hours or production data.</p>
          <div className="sp-hours-source">
            <label className={`sp-source-opt ${hoursSource === 'none' ? 'active' : ''}`}>
              <input type="radio" name="hoursSource" value="none" checked={hoursSource === 'none'} onChange={() => setHoursSource('none')} />
              <span className="sp-source-icon">📄</span>
              <div>
                <strong>Scope Only — No Hours</strong>
                <span>Scope notes, exclusions, access and photos only</span>
              </div>
            </label>
            <label className={`sp-source-opt ${hoursSource === 'measured' ? 'active' : ''}`}>
              <input type="radio" name="hoursSource" value="measured" checked={hoursSource === 'measured'} onChange={() => setHoursSource('measured')} />
              <span className="sp-source-icon">📏</span>
              <div>
                <strong>Measured Hours</strong>
                <span>Calculated from surface measurements and production rates</span>
              </div>
            </label>
            <label className={`sp-source-opt ${hoursSource === 'assessed' ? 'active' : ''} ${!hasAssessed ? 'sp-disabled' : ''}`}>
              <input type="radio" name="hoursSource" value="assessed" checked={hoursSource === 'assessed'} onChange={() => hasAssessed && setHoursSource('assessed')} disabled={!hasAssessed} />
              <span className="sp-source-icon">📋</span>
              <div>
                <strong>Assessed Hours</strong>
                <span>{hasAssessed ? 'PM experience-based assessment per surface' : 'No assessment data available'}</span>
              </div>
            </label>
            <label className={`sp-source-opt ${hoursSource === 'approved' ? 'active' : ''} ${!hasApproved ? 'sp-disabled' : ''}`}>
              <input type="radio" name="hoursSource" value="approved" checked={hoursSource === 'approved'} onChange={() => hasApproved && setHoursSource('approved')} disabled={!hasApproved} />
              <span className="sp-source-icon">✅</span>
              <div>
                <strong>Estimated Hours</strong>
                <span>{hasApproved ? 'Final estimated hours from review' : 'No estimated hours available'}</span>
              </div>
            </label>
          </div>
        </div>

        {/* ── Display Options — column & surface toggles ── */}
        {hoursSource !== 'none' && (
          <div className="sp-section">
            <div className="sp-section-title">⚙ Display Options</div>
            <p className="sp-hint">Toggle which data columns appear in the PDF tables. At least one column must be active when surface breakdown is shown.</p>

            {/* Column toggles — horizontal pill row */}
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
              {[
                { label: 'm²',     active: showM2,     toggle: () => setShowM2(v => { const next = !v; if (!next && !showHours && !showLitres) setShowSurfaceDetail(false); return next; }) },
                { label: 'Hours',   active: showHours,  toggle: () => setShowHours(v => { const next = !v; if (!next && !showM2 && !showLitres) setShowSurfaceDetail(false); return next; }) },
                { label: 'Litres',  active: showLitres,  toggle: () => setShowLitres(v => { const next = !v; if (!next && !showM2 && !showHours) setShowSurfaceDetail(false); return next; }) },
              ].map(col => (
                <button
                  key={col.label}
                  onClick={col.toggle}
                  style={{
                    padding:'6px 16px',borderRadius:20,fontSize:13,fontWeight:600,cursor:'pointer',
                    border: col.active ? '2px solid #5B2D82' : '2px solid #cbd5e1',
                    background: col.active ? '#5B2D82' : '#fff',
                    color: col.active ? '#fff' : '#64748b',
                    transition:'all 0.15s ease',
                  }}
                >{col.active ? '✓ ' : ''}{col.label}</button>
              ))}
            </div>

            {/* Surface breakdown toggle */}
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <button
                onClick={() => {
                  if (showSurfaceDetail) { setShowSurfaceDetail(false); }
                  else if (showM2 || showHours || showLitres) { setShowSurfaceDetail(true); }
                }}
                style={{
                  padding:'6px 16px',borderRadius:20,fontSize:13,fontWeight:600,cursor:'pointer',
                  border: showSurfaceDetail ? '2px solid #5B2D82' : '2px solid #cbd5e1',
                  background: showSurfaceDetail ? '#5B2D82' : '#fff',
                  color: showSurfaceDetail ? '#fff' : '#64748b',
                  opacity: (!showM2 && !showHours && !showLitres) ? 0.5 : 1,
                  transition:'all 0.15s ease',
                }}
              >{showSurfaceDetail ? '✓ ' : ''}Show surface breakdown</button>
              {!showM2 && !showHours && !showLitres && (
                <span style={{fontSize:12,color:'#dc2626'}}>Enable at least one data column to show surface breakdown</span>
              )}
            </div>
          </div>
        )}

        {/* ── Production Summary ── */}
        {(() => {
          const ps = calcProductionSummary(areas.filter(a => a._include));
          const washHrs = ps.external.m2 > 0 ? ps.external.m2 / washRate : 0;
          return (
            <div className="sp-section">
              <div className="sp-section-title">⏱ Production Summary <span className="sp-optional">auto-calculated from measurements</span></div>
              <table style={{width:'100%',fontSize:13,borderCollapse:'collapse',marginTop:8}}>
                <thead><tr style={{borderBottom:'2px solid #e2e8f0',textAlign:'left'}}>
                  <th style={{padding:'6px 8px'}}></th>
                  <th style={{padding:'6px 8px'}}>Hours</th>
                  <th style={{padding:'6px 8px'}}>Litres</th>
                  <th style={{padding:'6px 8px'}}>m²</th>
                </tr></thead>
                <tbody>
                  <tr style={{borderBottom:'1px solid #f0f0f0'}}>
                    <td style={{padding:'6px 8px',fontWeight:600}}>Internal</td>
                    <td style={{padding:'6px 8px'}}>{(ps.internal.hours + ps.internal.downtime).toFixed(1)}</td>
                    <td style={{padding:'6px 8px'}}>{ps.internal.litres.toFixed(0)}</td>
                    <td style={{padding:'6px 8px'}}>{ps.internal.m2.toFixed(0)}</td>
                  </tr>
                  <tr style={{borderBottom:'1px solid #f0f0f0'}}>
                    <td style={{padding:'6px 8px',fontWeight:600}}>External</td>
                    <td style={{padding:'6px 8px'}}>{(ps.external.hours + ps.external.downtime).toFixed(1)}</td>
                    <td style={{padding:'6px 8px'}}>{ps.external.litres.toFixed(0)}</td>
                    <td style={{padding:'6px 8px'}}>{ps.external.m2.toFixed(0)}</td>
                  </tr>
                  {washHrs > 0 && <tr style={{borderBottom:'1px solid #f0f0f0'}}>
                    <td style={{padding:'6px 8px',fontWeight:600}}>Ext. Wash</td>
                    <td style={{padding:'6px 8px'}}>{washHrs.toFixed(1)}</td>
                    <td colSpan={2}></td>
                  </tr>}
                  <tr style={{borderTop:'2px solid #5B2D82',fontWeight:700,color:'#5B2D82'}}>
                    <td style={{padding:'6px 8px'}}>Total</td>
                    <td style={{padding:'6px 8px'}}>{(ps.grandTotalHours + ps.totalDowntime + washHrs).toFixed(1)}</td>
                    <td style={{padding:'6px 8px'}}>{ps.totalLitres.toFixed(0)}</td>
                    <td style={{padding:'6px 8px'}}>{(ps.internal.m2 + ps.external.m2).toFixed(0)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="sp-field" style={{marginTop:12}}>
                <label>External wash rate (m²/hr)</label>
                <input type="number" className="sp-hours-input" value={washRate} min={1}
                  onChange={e => setWashRate(Math.max(1, parseInt(e.target.value) || 75))} style={{width:80}} />
              </div>
            </div>
          );
        })()}

        {/* ── Project summary ── */}
        <div className="sp-section">
          <div className="sp-section-title">📍 Project Details</div>
          <div className="sp-meta-grid">
            {[
              ['Client',    project.clientName],
              ['Address',   project.address],
              ['Job No.',   project.jobNumber],
              ['Estimator', project.estimatorName],
              ['Date',      project.date],
            ].filter(([, v]) => v).map(([label, value]) => (
              <div key={label} className="sp-meta-row">
                <span className="sp-meta-label">{label}</span>
                <span className="sp-meta-value">{value}</span>
              </div>
            ))}
          </div>
          <p className="sp-hint">Edit these details in the project settings before generating.</p>
        </div>

        {/* ── Areas ── */}
        <div className="sp-section">
          <div className="sp-section-title">🏠 Areas ({areas.length})</div>
          <p className="sp-hint">Toggle, reorder, edit notes, or remove photos before generating.</p>

          {areas.map((area, idx) => {
            const isExpanded = expandedAreas[idx];
            return (
              <div key={area.firestoreId || area.id || idx} className={`sp-area-card ${!area._include ? 'sp-excluded' : ''}`}>

                {/* Area header row */}
                <div className="sp-area-hdr">
                  <div className="sp-area-left">
                    {/* Include toggle */}
                    <button
                      className={`sp-toggle ${area._include ? 'on' : 'off'}`}
                      onClick={() => toggleInclude(idx)}
                      title={area._include ? 'Click to exclude from PDF' : 'Click to include in PDF'}
                    >
                      {area._include ? '✓' : '—'}
                    </button>
                    <div className="sp-area-name-wrap">
                      <span className="sp-area-icon">{area.type === 'internal' ? '🏠' : '🏗️'}</span>
                      <span className="sp-area-name">{area.name}</span>
                      {area.tag && <span className="sp-area-tag">{area.tag}</span>}
                    </div>
                  </div>

                  <div className="sp-area-right">
                    {/* Photo count badge */}
                    {area.photos.length > 0 && (
                      <span className="sp-photo-badge">📷 {area.photos.length}</span>
                    )}
                    {/* Reorder */}
                    <button className="sp-order-btn" onClick={() => moveArea(idx, -1)} disabled={idx === 0} title="Move up">↑</button>
                    <button className="sp-order-btn" onClick={() => moveArea(idx, 1)} disabled={idx === areas.length - 1} title="Move down">↓</button>
                    {/* Expand */}
                    <button className="sp-expand-btn" onClick={() => toggleExpand(idx)}>
                      {isExpanded ? '▲ Less' : '▼ Edit'}
                    </button>
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="sp-area-expanded">

                    {/* Scope notes */}
                    <div className="sp-field">
                      <label>Scope Notes</label>
                      <textarea
                        className="sp-textarea sp-textarea-sm"
                        rows={3}
                        placeholder="e.g. Like-for-like, 2 coats..."
                        value={area.scopeNotes || ''}
                        onChange={e => updateNotes(idx, e.target.value)}
                      />
                    </div>

                    {/* Photos */}
                    {area.photos.length > 0 && (
                      <div className="sp-field">
                        <label>Photos <span className="sp-optional">★ to set cover photo · × to remove from PDF</span></label>
                        <div className="sp-photo-strip">
                          {area.photos.map((photo, pi) => (
                            <div key={photo.id} className={`sp-photo-item ${photo.isProjectHero ? 'sp-cover-hero' : ''}`}>
                              <img src={photo.url} alt="" />
                              {photo.isProjectHero && <div className="sp-hero-badge">★ Cover</div>}
                              <button
                                className={`sp-star-btn ${photo.isProjectHero ? 'active' : ''}`}
                                onClick={() => setHeroPhoto(idx, photo.id)}
                                title={photo.isProjectHero ? 'Remove as cover photo' : 'Set as PDF cover photo'}
                              >★</button>
                              <button
                                className="sp-photo-remove"
                                onClick={() => removePhoto(idx, photo.id)}
                                title="Remove from PDF"
                              >×</button>
                            </div>
                          ))}
                        </div>
                        <p className="sp-hint">★ marks a photo as the cover image — it will appear on the PDF front page.</p>
                      </div>
                    )}

                    {area.photos.length === 0 && (
                      <div className="sp-no-photos">No photos for this area</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Access, Safety & Building — editable context for PDF ── */}
        {(() => {
          const allOpts = ACCESS_CATEGORIES.flatMap(c => c.options.map(o => ({ ...o, category: c.label })));
          const includedAreas = areas.filter(a => a._include);
          const allNeeds = [...new Set(includedAreas.flatMap(a => a.accessNeeds || []))];
          if (allNeeds.length === 0) return null;

          // Collect all custom other labels across areas
          const allOtherLabels = {};
          includedAreas.forEach(a => { Object.assign(allOtherLabels, a.accessOtherLabels || {}); });

          const grouped = {};
          allNeeds.forEach(id => {
            // Standard option
            const opt = allOpts.find(o => o.id === id);
            if (opt) {
              if (!grouped[opt.category]) grouped[opt.category] = [];
              grouped[opt.category].push({ id, label: opt.label });
              return;
            }
            // Dynamic "other" — match by prefix
            if (id.startsWith('other_')) {
              const cat = ACCESS_CATEGORIES.find(c => c.otherBase && id.startsWith(c.otherBase));
              if (cat) {
                if (!grouped[cat.label]) grouped[cat.label] = [];
                grouped[cat.label].push({ id, label: allOtherLabels[id] || `Other (${cat.label})` });
              }
            }
          });

          return (
            <div className="sp-section">
              <div className="sp-section-title">🦺 Access, Safety &amp; Building</div>
              <p className="sp-hint">
                These items were tagged during measurement. Edit the context sentence for each category
                to add site-specific details before exporting. These appear in each area section of the PDF.
              </p>
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} className="sp-access-cat">
                  <div className="sp-access-cat-title">{cat}</div>
                  <div className="sp-access-tags">
                    {items.map(o => (
                      <span key={o.id} className="sp-access-tag">{o.label}</span>
                    ))}
                  </div>
                  <textarea
                    className="sp-textarea sp-textarea-sm"
                    rows={2}
                    value={accessContexts[cat] || ''}
                    onChange={e => setAccessContexts(prev => ({ ...prev, [cat]: e.target.value }))}
                    placeholder={`Context for ${cat}...`}
                  />
                  <div className="sp-access-per-area">
                    {includedAreas.filter(a => (a.accessNeeds||[]).some(aid => items.some(o => o.id === aid))).map(a => (
                      <span key={a.firestoreId||a.id} className="sp-access-area-badge">
                        {a.type === 'internal' ? '🏠' : '🏗️'} {a.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Generate button (bottom) */}
        <div className="sp-bottom-bar">
          <button
            className="sp-generate-btn sp-generate-large"
            onClick={handleGenerate}
            disabled={exporting || includedCount === 0}
          >
            {exporting ? <><span className="spinner spinner-sm spinner-white"></span> {statusMsg}</> : <><Icon name="file" size={14} /> Generate PDF — {includedCount} areas</>}
          </button>
          {includedCount === 0 && (
            <p className="sp-warn">Include at least one area to generate a PDF.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScopePDFPreview;
